import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardHeader,
  CardContent,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  TablePagination,
  Grid,
  Button,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Page from '../../../components/Page';
import { useNavigate } from 'react-router-dom';
import { config } from '../../../utils/utils';
import Loader from '../../../components/Loader';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import KpiSummary from '../../../components/KpiSummary';
import KpiPdfExport from '../../../components/KpiPdfExport';
import { SuperUserRole } from '../../../enum';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register ChartJS components and plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ChartDataLabels);

// Utility function for consistent date formatting
const formatDateToYYYYMMDD = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Generate an array of months between two dates or last 12 months by default
const generateMonthsBetweenDates = (startDate: Date | null = null, endDate: Date | null = null) => {
  const months: Array<{
    month: string;
    year: number;
    monthNumber: number;
    fullLabel: string;
  }> = [];
  const now = new Date();

  // Always generate the last 12 months by default
  const generateLast12Months = () => {
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
  
    // Get the last 12 months (including current month)
    for (let i = 11; i >= 0; i--) {
      let month = currentMonth - i;
      let year = currentYear;
      
      // Adjust if we go to previous year
      if (month < 0) {
        month = 12 + month; // e.g., -1 becomes 11 (December)
        year = year - 1;
      }
      
      const monthName = new Date(year, month, 1).toLocaleString('default', { month: 'short' });
      months.push({
        month: monthName,
        year: year,
        monthNumber: month + 1,
        fullLabel: `${monthName} ${year}`
      });
    }
    
    return months.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year;
      }
      return a.monthNumber - b.monthNumber;
    });
  };

  // If no dates provided, use last 12 months
  if (!startDate || !endDate) {
    return generateLast12Months();
  }
   
  // Use the provided date range
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Set to first day of month
  start.setDate(1);
  end.setDate(1);
  
  // Loop through all months between start and end dates
  const currentDate = new Date(start);
  while (currentDate <= end) {
    const monthName = currentDate.toLocaleString('default', { month: 'short' });
    const year = currentDate.getFullYear();
    const monthNumber = currentDate.getMonth() + 1;
    
    months.push({
      month: monthName,
      year: year,
      monthNumber: monthNumber,
      fullLabel: `${monthName} ${year}`
    });
    
    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  // Return exactly the months in the selected date range without enforcing a minimum of 12
  return months.sort((a, b) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.monthNumber - b.monthNumber;
  });
};

interface Advertiser {
  advertiserId: string;
  name: string;
  userRole: string;
  totalViews: number;
  totalContactClicks: number;
  adsPublished: number;
}

interface AdvertiserPerformanceResponse {
  totalAdvertisers: number;
  advertisers: Advertiser[];
  startDate: string;
  endDate: string;
  monthsInRange: {month: number, year: number}[];
  skip: number;
  limit: number;
}

interface RootState {
  auth: {
    accessToken: string;
  };
}

// Monthly Registration Data Interface
interface MonthlyRegistrationData {
  month: string;
  year: number;
  monthNumber: number;
  individual: number;
  company: number;
  total: number;
  monthYear: string;
}

// Monthly Published Ads Data Interface
interface MonthlyPublishedAdsData {
  month: string;
  year: number;
  monthNumber: number;
  individual: number;
  company: number;
  total: number;
  monthYear: string;
}

// Ads Monthly Status Data Interface
interface AdsMonthlyStatusData {
  month: string;
  year: number;
  monthNumber: number;
  published: number;
  rejected: number;
  suspended: number;
  total: number;
  monthYear: string;
}

// Function to prepare chart data with 12-month defaults
const prepareChartData = (apiData: any[], twelveMonths: any[]) => {
  const resultData: Record<string, any> = {};
  
  // Use the provided months array, which could be any length based on selected date range
  const monthsToUse = twelveMonths;
  
  // Initialize all 12 months with zero values
  monthsToUse.forEach(month => {
    const key = `${month.year}-${month.monthNumber}`;
    resultData[key] = {
      month: month.month,
      year: month.year,
      monthNumber: month.monthNumber,
      individual: 0,
      company: 0,
      published: 0,
      rejected: 0,
      suspended: 0,
      total: 0,
      monthYear: month.fullLabel
    };
  });
  
  // Fill in actual data from API where available
  if (apiData && apiData.length > 0) {
    apiData.forEach(item => {
      const key = `${item.year}-${item.monthNumber}`;
      if (resultData[key]) {
        // Copy all properties from API data
        Object.keys(item).forEach(prop => {
          if (prop !== 'month' && prop !== 'year' && prop !== 'monthNumber' && prop !== 'monthYear') {
            resultData[key][prop] = item[prop];
          }
        });
      }
    });
  }
  
  // Convert to array and sort by year and month
  return Object.values(resultData).sort((a: any, b: any) => {
    if (a.year !== b.year) {
      return a.year - b.year;
    }
    return a.monthNumber - b.monthNumber;
  });
};

const AdvertiserPerformance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AdvertiserPerformanceResponse | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter states
  const [selectedRoleType, setSelectedRoleType] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [usersList, setUsersList] = useState<Array<{id: string, name: string, userRole?: string}>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Get months period for charts - initially last 12 months
  const [chartMonths, setChartMonths] = useState(generateMonthsBetweenDates());
  
  // Monthly data states
  const [monthlyUserRegistration, setMonthlyUserRegistration] = useState<MonthlyRegistrationData[]>([]);
  const [monthlyPublishedAds, setMonthlyPublishedAds] = useState<MonthlyPublishedAdsData[]>([]);
  const [adsMonthlyStatus, setAdsMonthlyStatus] = useState<AdsMonthlyStatusData[]>([]);
  const [loadingCharts, setLoadingCharts] = useState<boolean>(false);
  
  // Refs for chart containers
  const chart1Ref = useRef<HTMLDivElement>(null);
  const chart2Ref = useRef<HTMLDivElement>(null);
  const chart3Ref = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  
  // Get current date for initial date range
  const now = new Date();
  // Set to first day of current month
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // Set to last day of current month
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  const [startDate, setStartDate] = useState<Date | null>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(lastDayOfMonth);
  // Adding applied date states that will only update when filter is applied
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(firstDayOfMonth);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(lastDayOfMonth);
  const [dateError, setDateError] = useState<string | null>(null);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  
  // Validate date range
  const validateDateRange = (start: Date | null, end: Date | null): boolean => {
    if (!start || !end) {
      setDateError('Both start and end dates are required');
      return false;
    }
    
    if (start > end) {
      setDateError('Start date cannot be after end date');
      return false;
    }
    
    setDateError(null);
    return true;
  };
  
  // Handle start date change with validation
  const handleStartDateChange = (value: Date | null) => {
    setStartDate(value);
    if (value && endDate) {
      validateDateRange(value, endDate);
    }
  };
  
  // Handle end date change with validation
  const handleEndDateChange = (value: Date | null) => {
    setEndDate(value);
    if (startDate && value) {
      validateDateRange(startDate, value);
    }
  };
  
  useEffect(() => {
    if (!accessToken) {
      navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
    }
  }, [accessToken, navigate]);
  
  // Initial data load when component mounts
  useEffect(() => {
    if (accessToken) {
      // Initial data load only when component mounts
      const initialLoad = async () => {
        setLoading(true);
        setLoadingCharts(true);
        
        try {
          // Prepare params for API calls
          const params = new URLSearchParams();
          params.append('skip', (page * rowsPerPage).toString());
          params.append('limit', rowsPerPage.toString());
          
          if (appliedStartDate) {
            params.append('startDate', formatDateToYYYYMMDD(appliedStartDate));
          }
          if (appliedEndDate) {
            params.append('endDate', formatDateToYYYYMMDD(appliedEndDate));
          }
          
          // Add role and user filters
          if (selectedRoleType) {
            params.append('userRole', selectedRoleType);
          }
          if (selectedUserId) {
            params.append('advertiserId', selectedUserId);
          }
          
          // Get months for the current date range
          const initialChartMonths = generateMonthsBetweenDates(appliedStartDate, appliedEndDate);
          setChartMonths(initialChartMonths);
          
          // Fetch all data in parallel
          const [
            response,
            registrationResponse,
            publishedAdsResponse,
            adsStatusResponse
          ] = await Promise.all([
            // Fetch advertiser data
            fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Monthly User Registration data
            fetch(`${config.BASE_URL}/monthly-user-registration?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Monthly Published Ads data
            fetch(`${config.BASE_URL}/monthly-published-ads?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Ads Monthly Status data
            fetch(`${config.BASE_URL}/ads-monthly-status?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            })
          ]);
          
          // Handle authentication errors
          if (response.status === 401) {
            navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
            return;
          }
          
          // Check if all responses are OK
          if (!response.ok || !registrationResponse.ok || !publishedAdsResponse.ok || !adsStatusResponse.ok) {
            throw new Error('Failed to fetch data');
          }
          
          // Parse all responses
          const [
            result,
            registrationData,
            publishedAdsData,
            adsStatusData
          ] = await Promise.all([
            response.json(),
            registrationResponse.json(),
            publishedAdsResponse.json(),
            adsStatusResponse.json()
          ]);
          
          // Process the API data with current chart months
          const processedRegistrationData = prepareChartData(registrationData.data, initialChartMonths);
          const processedPublishedAdsData = prepareChartData(publishedAdsData.data, initialChartMonths);
          const processedAdsStatusData = prepareChartData(adsStatusData.data, initialChartMonths);
          
          // After all data is successfully fetched, update all state at once
          setData(result);
          setMonthlyUserRegistration(processedRegistrationData);
          setMonthlyPublishedAds(processedPublishedAdsData);
          setAdsMonthlyStatus(processedAdsStatusData);
          
        } catch (error) {
          console.error('Error fetching initial data:', error);
        } finally {
          setLoading(false);
          setLoadingCharts(false);
        }
      };
      
      initialLoad();
    }
  }, [accessToken, appliedStartDate, appliedEndDate, selectedRoleType, selectedUserId]);
  
  // Separate effect for pagination to avoid refetching charts
  useEffect(() => {
    if (accessToken && data) {
      const fetchPaginatedData = async () => {
        setLoading(true);
        try {
          const params = new URLSearchParams();
          params.append('skip', (page * rowsPerPage).toString());
          params.append('limit', rowsPerPage.toString());
          
          if (appliedStartDate) {
            params.append('startDate', formatDateToYYYYMMDD(appliedStartDate));
          }
          if (appliedEndDate) {
            params.append('endDate', formatDateToYYYYMMDD(appliedEndDate));
          }
          
          if (selectedRoleType) {
            params.append('userRole', selectedRoleType);
          }
          if (selectedUserId) {
            params.append('advertiserId', selectedUserId);
          }
          
          const response = await fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.status === 401) {
            navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
            return;
          }
          
          if (!response.ok) {
            throw new Error('Failed to fetch data');
          }
          
          const result = await response.json();
          setData(result);
        } catch (error) {
          console.error('Error fetching paginated data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchPaginatedData();
    }
  }, [page, rowsPerPage]);
  
  // Load data when page or rows per page changes, but not when dates change
  useEffect(() => {
    if (accessToken) {
      // Create a function to fetch just the paginated data
      const fetchPaginatedData = async () => {
        setLoading(true);
        try {
          // Prepare params for API call
          const params = new URLSearchParams();
          params.append('skip', (page * rowsPerPage).toString());
          params.append('limit', rowsPerPage.toString());
          
          if (appliedStartDate) {
            params.append('startDate', formatDateToYYYYMMDD(appliedStartDate));
          }
          if (appliedEndDate) {
            params.append('endDate', formatDateToYYYYMMDD(appliedEndDate));
          }
          
          // Add role and user filters
          if (selectedRoleType) {
            params.append('userRole', selectedRoleType);
          }
          if (selectedUserId) {
            params.append('advertiserId', selectedUserId);
          }
          
          // Fetch paginated advertiser data
          const response = await fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Handle authentication errors
          if (response.status === 401) {
            navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
            return;
          }
          
          // Check if response is OK
          if (!response.ok) {
            throw new Error('Failed to fetch data');
          }
          
          // Parse response
          const result = await response.json();
          
          // Update paginated data
          setData(result);
          
        } catch (error) {
          console.error('Error fetching paginated data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchPaginatedData();
    }
  }, [page, rowsPerPage]);
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleViewDetails = (advertiserId: string) => {
    const params = new URLSearchParams();
    if (appliedStartDate) {
      params.append('startDate', formatDateToYYYYMMDD(appliedStartDate));
    }
    if (appliedEndDate) {
      params.append('endDate', formatDateToYYYYMMDD(appliedEndDate));
    }
    const query = params.toString();
    navigate(`/kpi/advertiser-detail/${advertiserId}${query ? `?${query}` : ''}`);
  };

  // Fetch users based on role type
  const fetchUsersForRole = async (role: string) => {
    if (!role || !accessToken) {
      setUsersList([]);
      return;
    }

    setLoadingUsers(true);
    try {
      // When "Company" is selected, fetch only company users
      if (role === SuperUserRole.company) {
        const response = await fetch(
          `${config.BASE_URL}/users?userRole=${SuperUserRole.company}&limit=100`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 401) {
          navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setUsersList(data.data || []);
        } else {
          setUsersList([]);
        }
      } else {
        // For other roles, fetch normally with role filter
      const response = await fetch(
        `${config.BASE_URL}/users?userRole=${role}&limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 401) {
        navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setUsersList(data.data || []);
      } else {
        setUsersList([]);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsersList([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle role type change
  const handleRoleTypeChange = (role: string) => {
    setSelectedRoleType(role);
    setSelectedUserId(''); // Reset user selection
    setUsersList([]); // Clear users list
    
    if (role) {
      fetchUsersForRole(role);
    }
  };

  // Handle user selection change
  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  // Function to fetch all advertisers data for PDF export
  const fetchAllAdvertisersData = async (): Promise<AdvertiserPerformanceResponse | null> => {
    try {
      const params = new URLSearchParams();
      params.append('skip', '0');
      params.append('limit', '10000'); // Large limit to get all data
      
      if (appliedStartDate) {
        params.append('startDate', formatDateToYYYYMMDD(appliedStartDate));
      }
      if (appliedEndDate) {
        params.append('endDate', formatDateToYYYYMMDD(appliedEndDate));
      }
      
      // Add role and user filters for PDF export
      if (selectedRoleType) {
        params.append('userRole', selectedRoleType);
      }
      if (selectedUserId) {
        params.append('advertiserId', selectedUserId);
      }
      
      const response = await fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        return result;
      }
      return null;
    } catch (error) {
      console.error('Error fetching all advertisers data:', error);
      return null;
    }
  };

  const handleApplyFilters = () => {
    if (validateDateRange(startDate, endDate)) {
      // Always use exactly the months between selected start and end dates
      const newChartMonths = generateMonthsBetweenDates(startDate, endDate);
      
      // Start loading indicator
      setLoading(true);
      setLoadingCharts(true);
      
      // Create a function to handle all API calls with direct parameters instead of relying on state
      const fetchAllData = async () => {
        try {
          // Use local references to parameters rather than state variables
          const currentStartDate = startDate;
          const currentEndDate = endDate;
          
          // Prepare params for API calls
          const params = new URLSearchParams();
          params.append('skip', (page * rowsPerPage).toString());
          params.append('limit', rowsPerPage.toString());
          
          if (currentStartDate) {
            params.append('startDate', formatDateToYYYYMMDD(currentStartDate));
          }
          if (currentEndDate) {
            params.append('endDate', formatDateToYYYYMMDD(currentEndDate));
          }
          
          // Fetch all data in parallel
          const [
            response,
            registrationResponse,
            publishedAdsResponse,
            adsStatusResponse
          ] = await Promise.all([
            // Fetch advertiser data
            fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Monthly User Registration data
            fetch(`${config.BASE_URL}/monthly-user-registration?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Monthly Published Ads data
            fetch(`${config.BASE_URL}/monthly-published-ads?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }),
            
            // Fetch Ads Monthly Status data
            fetch(`${config.BASE_URL}/ads-monthly-status?${params.toString()}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            })
          ]);
          
          // Handle authentication errors
          if (response.status === 401) {
            navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
            return;
          }
          
          // Check if all responses are OK
          if (!response.ok || !registrationResponse.ok || !publishedAdsResponse.ok || !adsStatusResponse.ok) {
            throw new Error('Failed to fetch data');
          }
          
          // Parse all responses
          const [
            result,
            registrationData,
            publishedAdsData,
            adsStatusData
          ] = await Promise.all([
            response.json(),
            registrationResponse.json(),
            publishedAdsResponse.json(),
            adsStatusResponse.json()
          ]);
          
          // Process the API data with the new chart months
          const processedRegistrationData = prepareChartData(registrationData.data, newChartMonths);
          const processedPublishedAdsData = prepareChartData(publishedAdsData.data, newChartMonths);
          const processedAdsStatusData = prepareChartData(adsStatusData.data, newChartMonths);
          
          // After all data is successfully fetched, update all state at once
          setData(result);
          setMonthlyUserRegistration(processedRegistrationData);
          setMonthlyPublishedAds(processedPublishedAdsData);
          setAdsMonthlyStatus(processedAdsStatusData);
          setChartMonths(newChartMonths);
          setAppliedStartDate(currentStartDate);
          setAppliedEndDate(currentEndDate);
          
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
          setLoadingCharts(false);
        }
      };
      
      // Execute all API calls
      fetchAllData();
    }
  };

  const handleResetFilters = () => {
    // Get current date
    const now = new Date();
    // Set to first day of current month
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    // Set to last day of current month
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Update input field states immediately
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    setDateError(null); // Clear any date errors
    setPage(0);
    
    // Only show the current month in the reset view
    const resetChartMonths = generateMonthsBetweenDates(firstDayOfMonth, lastDayOfMonth);
    
    // Start loading indicators
    setLoading(true);
    setLoadingCharts(true);
    
    // Create a function to handle all API calls with direct parameters
    const fetchResetData = async () => {
      if (!accessToken) {
        navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
        return;
      }
      
      try {
        // Prepare params for API calls
        const params = new URLSearchParams();
        params.append('skip', '0'); // Reset to first page
        params.append('limit', rowsPerPage.toString());
        
        // Use date values directly with the utility function
        params.append('startDate', formatDateToYYYYMMDD(firstDayOfMonth));
        params.append('endDate', formatDateToYYYYMMDD(lastDayOfMonth));
        
        // Fetch all data in parallel
        const [
          response,
          registrationResponse,
          publishedAdsResponse,
          adsStatusResponse
        ] = await Promise.all([
          // Fetch advertiser data
          fetch(`${config.BASE_URL}/kpi/advertisers/performance?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }),
          
          // Fetch Monthly User Registration data
          fetch(`${config.BASE_URL}/monthly-user-registration?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }),
          
          // Fetch Monthly Published Ads data
          fetch(`${config.BASE_URL}/monthly-published-ads?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }),
          
          // Fetch Ads Monthly Status data
          fetch(`${config.BASE_URL}/ads-monthly-status?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          })
        ]);
        
        // Handle authentication errors
        if (response.status === 401) {
          navigate('/login', { state: { from: '/kpi/advertiser-performance' } });
          return;
        }
        
        // Check if all responses are OK
        if (!response.ok || !registrationResponse.ok || !publishedAdsResponse.ok || !adsStatusResponse.ok) {
          throw new Error('Failed to fetch data');
        }
        
        // Parse all responses
        const [
          result,
          registrationData,
          publishedAdsData,
          adsStatusData
        ] = await Promise.all([
          response.json(),
          registrationResponse.json(),
          publishedAdsResponse.json(),
          adsStatusResponse.json()
        ]);
        
        // Process the API data with the reset chart months
        const processedRegistrationData = prepareChartData(registrationData.data, resetChartMonths);
        const processedPublishedAdsData = prepareChartData(publishedAdsData.data, resetChartMonths);
        const processedAdsStatusData = prepareChartData(adsStatusData.data, resetChartMonths);
        
        // After all data is successfully fetched, update all state at once
        setData(result);
        setMonthlyUserRegistration(processedRegistrationData);
        setMonthlyPublishedAds(processedPublishedAdsData);
        setAdsMonthlyStatus(processedAdsStatusData);
        setChartMonths(resetChartMonths);
        setAppliedStartDate(firstDayOfMonth);
        setAppliedEndDate(lastDayOfMonth);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
        setLoadingCharts(false);
      }
    };
    
    // Execute all API calls
    fetchResetData();
  };

  // Common chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20, // Add padding to the legend labels
          boxWidth: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        enabled: true,
      },
      // Add plugin to display data values on top of bars
      datalabels: {
        display: function(context: any) {
          // Only display the label if the value is not null
          return context.dataset.data[context.dataIndex] !== null;
        },
        // Use black text color for all labels for better visibility
        color: '#000000',
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 8,
        // Only display non-null values
        formatter: function(value: number | null) {
          return value !== null ? value.toString() : '';
        },
        font: {
          weight: 'bold' as const,
          size: 16,
        },
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        // Add padding to the top to make room for data labels
        suggestedMax: function(context: any) {
          // Add 20% padding to the top
          const max = context.chart.data.datasets.reduce(
            (max: number, dataset: any) => {
              // Filter out null values when finding the max
              const filteredData = dataset.data.filter((value: any) => value !== null);
              const dataMax = filteredData.length > 0 ? Math.max(...filteredData) : 0;
              return dataMax > max ? dataMax : max;
            }, 0);
          return max * 1.2;
        },
        grid: {
          drawBorder: true,
          color: '#E2E2E2',
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false, // Ensure all labels are displayed
          font: {
            size: 12
          }
        },
        grid: {
          display: false
        }
      }
    },
    skipNull: true, // Skip null values in rendering
    // Adjust layout based on number of months
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 30,  // Provide more space for data labels
        bottom: 10
      }
    }
  };
  
  // Calculate optimal bar width based on month count
  const getOptimalBarPercentage = () => {
    if (chartMonths.length <= 3) return 0.5;
    if (chartMonths.length <= 6) return 0.7;
    return 0.8;
  };

  // Prepare monthly user registration chart data
  const monthlyUserRegistrationChart = {
    labels: monthlyUserRegistration.map(item => item.monthYear || `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Individual',
        data: monthlyUserRegistration.map(item => item.individual === 0 ? null : item.individual),
        backgroundColor: '#0000FF',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Company',
        data: monthlyUserRegistration.map(item => item.company === 0 ? null : item.company),
        backgroundColor: '#FFA500',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Total',
        data: monthlyUserRegistration.map(item => item.total === 0 ? null : item.total),
        backgroundColor: '#808080',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
    ],
  };

  // Prepare monthly published ads chart data
  const monthlyPublishedAdsChart = {
    labels: monthlyPublishedAds.map(item => item.monthYear || `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Individual',
        data: monthlyPublishedAds.map(item => item.individual === 0 ? null : item.individual),
        backgroundColor: '#0000FF',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Company',
        data: monthlyPublishedAds.map(item => item.company === 0 ? null : item.company),
        backgroundColor: '#FFA500',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Total',
        data: monthlyPublishedAds.map(item => item.total === 0 ? null : item.total),
        backgroundColor: '#808080',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
    ],
  };

  // Prepare ads monthly status chart data
  const adsMonthlyStatusChart = {
    labels: adsMonthlyStatus.map(item => item.monthYear || `${item.month} ${item.year}`),
    datasets: [
      {
        label: 'Published',
        data: adsMonthlyStatus.map(item => item.published === 0 ? null : item.published),
        backgroundColor: '#0000FF',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Rejected',
        data: adsMonthlyStatus.map(item => item.rejected === 0 ? null : item.rejected),
        backgroundColor: '#FFA500',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Suspended',
        data: adsMonthlyStatus.map(item => item.suspended === 0 ? null : item.suspended),
        backgroundColor: '#808080',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
      {
        label: 'Total',
        data: adsMonthlyStatus.map(item => item.total === 0 ? null : item.total),
        backgroundColor: '#9E0000',
        barPercentage: getOptimalBarPercentage(),
        categoryPercentage: 0.9,
      },
    ],
  };

  const getRoleChipColor = (role: string): 'primary' | 'success' | 'warning' | 'default' => {
    switch (role) {
      case 'company':
        return 'primary';
      case 'broker':
        return 'warning';
      case 'individual':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string): string => {
    if (role === 'individual') return 'Individual';
    if (role === 'broker') return 'Broker';
    if (role === 'company') return 'Company';
    return role;
  };

  const ALLOWED_TABLE_ROLES = ['individual', 'broker', 'company'];

  const tableAdvertisers = (data?.advertisers || []).filter((advertiser) =>
    ALLOWED_TABLE_ROLES.includes(advertiser.userRole)
  );

  return (
    <Page title="KPI Dashboard">
      <Container>
        <Box sx={{ pt: 3, pb: 3 }}>
          {/* <Typography variant="h4" gutterBottom>KPI Dashboard</Typography> */}
        
          {/* Date Range Filters - moved to the top */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Date Filters</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={handleStartDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth error={!!dateError} />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth error={!!dateError} />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<FilterListIcon />}
                        onClick={handleApplyFilters}
                        disabled={!!dateError}
                      >
                        Apply Filters
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleResetFilters}
                      >
                        Reset
                      </Button>
                    </Box>
                    {dateError && (
                      <Typography color="error" variant="caption">
                        {dateError}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
              
              {/* Role and User Filters */}
              <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="role-type-filter-label">Role Type</InputLabel>
                    <Select
                      labelId="role-type-filter-label"
                      id="role-type-filter"
                      value={selectedRoleType}
                      label="Role Type"
                      onChange={(e) => handleRoleTypeChange(e.target.value)}
                    >
                      <MenuItem value="">All Roles</MenuItem>
                      <MenuItem value={SuperUserRole.individual}>Individual</MenuItem>
                      <MenuItem value={SuperUserRole.company}>Company</MenuItem>
                      <MenuItem value={SuperUserRole.broker}>Broker</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" disabled={!selectedRoleType || loadingUsers}>
                    <InputLabel id="user-filter-label">User / Company / Broker</InputLabel>
                    <Select
                      labelId="user-filter-label"
                      id="user-filter"
                      value={selectedUserId}
                      label="User / Company / Broker"
                      onChange={(e) => handleUserChange(e.target.value)}
                    >
                      <MenuItem value="">All Users</MenuItem>
                      {usersList.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name || 'Unknown'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <KpiPdfExport
                    data={data}
                    startDate={appliedStartDate}
                    endDate={appliedEndDate}
                    chart1Ref={chart1Ref}
                    chart2Ref={chart2Ref}
                    chart3Ref={chart3Ref}
                    tableRef={tableRef}
                    loading={loading}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    formatDateToYYYYMMDD={formatDateToYYYYMMDD}
                    fetchAllData={fetchAllAdvertisersData}
                    accessToken={accessToken}
                    selectedRoleType={selectedRoleType}
                    selectedUserId={selectedUserId}
                    usersList={usersList}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* KPI Summary Section */}
          <KpiSummary 
            startDate={appliedStartDate ? formatDateToYYYYMMDD(appliedStartDate) : undefined} 
            endDate={appliedEndDate ? formatDateToYYYYMMDD(appliedEndDate) : undefined} 
          />

          {/* Monthly Charts Grid */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Monthly Registered Users Chart */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Monthly Registered Users" />
                <CardContent>
                  {loadingCharts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400 }}>
                      <Loader />
                    </Box>
                  ) : (
                    <Box ref={chart1Ref} sx={{ height: 400 }}>
                      <Bar data={monthlyUserRegistrationChart} options={chartOptions} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Monthly Published Ads Chart */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Monthly Published Ads" />
                <CardContent>
                  {loadingCharts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400 }}>
                      <Loader />
                    </Box>
                  ) : (
                    <Box ref={chart2Ref} sx={{ height: 400 }}>
                      <Bar data={monthlyPublishedAdsChart} options={chartOptions} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Ads Monthly Status Chart */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Ads Monthly Status" />
                <CardContent>
                  {loadingCharts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400 }}>
                      <Loader />
                    </Box>
                  ) : (
                    <Box ref={chart3Ref} sx={{ height: 400 }}>
                      <Bar data={adsMonthlyStatusChart} options={chartOptions} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Advertisers Table - Individuals, Brokers & Companies only (agents rolled into company) */}
          <Card>
            <CardHeader title="Advertisers (Individuals, Brokers & Companies)" />
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Loader />
              </Box>
            ) : data ? (
              <div ref={tableRef}>
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">S.No</TableCell>
                        <TableCell align="left">Advertiser</TableCell>
                        <TableCell align="center">Role</TableCell>
                        <TableCell align="center">Total Views</TableCell>
                        <TableCell align="center">Contact Clicks</TableCell>
                        <TableCell align="center">Ads Published</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableAdvertisers.length > 0 ? (
                      tableAdvertisers.map((advertiser, index) => (
                        <TableRow 
                          key={advertiser.advertiserId}
                          hover
                        >
                          <TableCell align="left">{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell align="left">
                            <Typography variant="subtitle2">
                              {advertiser.name || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={getRoleLabel(advertiser.userRole)} 
                              size="small" 
                              color={getRoleChipColor(advertiser.userRole)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">{advertiser.totalViews.toLocaleString()}</TableCell>
                          <TableCell align="center">{advertiser.totalContactClicks.toLocaleString()}</TableCell>
                          <TableCell align="center">{advertiser.adsPublished}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDetails(advertiser.advertiserId);
                                }}
                                size="small"
                                sx={{ color: '#808080' }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              No advertisers found for the selected filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={data.totalAdvertisers}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            ) : (
              <Typography variant="body1" align="center" sx={{ p: 3 }}>
                No data available
              </Typography>
            )}
          </Card>
        </Box>
      </Container>
    </Page>
  );
};

export default AdvertiserPerformance; 